import React, { useState } from "react";
import YearSelection from "./YearSelection";
import QuarterlySelection from "./QuarterlySelection";
import MonthSelection from "./MonthSelection";
import Const from "const.json";
import { ButtonDictionaryItem } from "types/ButtonDictionaryItem";
import "./PeriodSelection.scss";
import { PeriodState } from "types/PeriodSelectionTypes";
import { MediaQuery, MonthEnum, SelectionTypeEnum, SelectionTypeIndexEnum } from "enums/enums";
import GroupButtons from "components/controls/GroupButtons/GroupButtons";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { Button, Modal } from "react-bootstrap";
import { setPeriod } from "../../../redux/generalReducer";
import useMediaQuery from "hooks/useMediaQuery";
import * as Utils from "utils/utils";
import { handleKeyDownEvent, renderPeriod } from "utils/utilsLogic";
import CustomTooltip from "components/controls/CustomTooltip/CustomTooltip";
import Popup from "components/controls/PopUp/PopUp";

const defaultPeriod: PeriodState = {
  year: { text: '', indexes: null },
  month: { text: '', indexes: null },
  quarterly: { text: '', indexes: null },
  active: SelectionTypeIndexEnum.year
}

type PeriodSelectionProps = {
  onChange: (type: string, value: string, valueIndexes: number[]) => void;
  unsavedPeriods: PeriodState;
  closePopup: () => void;
  setUnsavedPeriods: (value: PeriodState) => void;
};

const PeriodSelection: React.FC<PeriodSelectionProps> = (PeriodSelection) => {
  const period = useAppSelector((state) => state.general.period);
  const [chosenPeriodIndex, setChosenType] = useState(period.active);
  const [showError, setShowError] = useState(false);
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery(MediaQuery.Mobile);

  const handleSelectionChange = (
    type: SelectionTypeEnum,
    keys: MonthEnum | MonthEnum[],
    valueString: string,
    valueIndexes: number[]
  ) => {
    PeriodSelection.onChange(type, valueString, valueIndexes);
  };
  const buttonDictionary: ButtonDictionaryItem[] = [
    {
      label: Const.year,
      component: (
        <YearSelection
          onSelectionChange={handleSelectionChange}
          indexes={period.year.indexes}
        />
      ),
    },
    {
      label: Const.quarterly,
      component: (
        <QuarterlySelection
          onSelectionChange={handleSelectionChange}
          indexes={period.quarterly.indexes}
        />
      ),
    },
    {
      label: Const.month,
      component: (
        <MonthSelection
          onSelectionChange={handleSelectionChange}
          indexes={period.month.indexes}
        />
      ),
    },
  ];

  const hasConsecutiveGroupOverAndValidMax = () => {
    const { month, quarterly } = PeriodSelection.unsavedPeriods;

    const indexes =
      chosenPeriodIndex === SelectionTypeIndexEnum.month
        ? month.indexes
        : chosenPeriodIndex === SelectionTypeIndexEnum.quarterly
          ? quarterly.indexes
          : null;

    // Return false if indexes are null or empty
    if (!indexes || indexes.length === 0) return false;

    const sortedArray = [...indexes].sort((a, b) => a - b);

    const maxSelection =
      chosenPeriodIndex === SelectionTypeIndexEnum.month
        ? Const.MONTH_MAX_SELECTIONS
        : chosenPeriodIndex === SelectionTypeIndexEnum.quarterly
          ? Const.QUARTERLY_MAX_SELECTIONS
          : 0;


    // Check the count of indexes
    if (sortedArray.length > maxSelection) {
      return true;
    }

    // Check if all values are consecutive
    for (let i = 0; i < sortedArray.length - 1; i++) {
      if (sortedArray[i + 1] !== sortedArray[i] + 1) {
        return true;
      }
    }

    return false;
  };

  const saveSelectedPeriods = () => {
    if (hasConsecutiveGroupOverAndValidMax()) {
      setShowError(true);
    }
    else {
      const chosenPeriod = SelectionTypeIndexEnum[chosenPeriodIndex] as keyof PeriodState
      const chosenValues = PeriodSelection.unsavedPeriods[chosenPeriod]
      dispatch(setPeriod({
        ...defaultPeriod,
        year: PeriodSelection.unsavedPeriods['year'],
        [chosenPeriod]: chosenValues,
        active: chosenPeriodIndex
      }));
      PeriodSelection.closePopup();
    }
  };

  return (
    <>
      <Modal aria-label={Const.periodSelection}
        backdropClassName="period-modal-backdrop"
        className="period-modal"
        show={true}
        fullscreen={isMobile || ''}
        onHide={() => PeriodSelection.closePopup()}>
        <Modal.Header closeButton closeLabel={Const.closeWindow}>
        </Modal.Header>
        {/* Body */}
        <Modal.Body>
          {isMobile &&
            <div className="periodSelection d-flex periodSelection-mobile">
              <div className="periodSelection-mobile-year periodSelection-mobile-text">
                {renderPeriod(
                  Const.yearOf,
                  period.year.text,
                  `${Const.yearOf} ${Utils.currentYear}`
                )}
              </div>
              <div className="periodSelection-mobile-text">{renderPeriod(Const.quarters, period.quarterly.text)}</div>
              <div className="periodSelection-mobile-text">{renderPeriod(Const.months, period.month.text)}</div>
              <div>&nbsp;{`${Const.updateToDate}${Utils.formatDate()}`}</div>
            </div>}
          <div className="d-flex justify-content-center align-items-center">
            <p className='custom-period-selector-label '>{Const.periodSelection}</p>
            <CustomTooltip content={Const.periodSelectionTooltip} />
          </div>
          <GroupButtons selectedIndex={chosenPeriodIndex} setCurrentIndex={setChosenType} buttonNames={buttonDictionary} alwaysDisplayIndex={0} />
          {!isMobile && <Button className="blue-button m-auto mt-2"
            onClick={saveSelectedPeriods} aria-label={Const.displayForPeriod} aria-description={Const.pageWillShowInThaPage}
            onKeyDown={(event) => handleKeyDownEvent(event, () => saveSelectedPeriods())}
          >
            {Const.forDisplay}
          </Button>}
        </Modal.Body>
        {/* Footer */}
        {isMobile && <Modal.Footer className="d-flex justify-content-between">
          <Button className="  bold text-decoration-underline" variant="secondary">
            {Const.clear}
          </Button>
          <Button className="py-3 px-5 border rounded-3" variant="primary"
            onClick={saveSelectedPeriods} aria-description={Const.pageWillShowInThaPage}
            onKeyDown={(event) => handleKeyDownEvent(event, () => saveSelectedPeriods())}
          >
            {Const.forDisplay}
          </Button>
        </Modal.Footer>}
      </Modal>
      <Popup body={Const.periodPopup} show={showError} setShow={setShowError} />
    </>
  );
};

export default PeriodSelection;